/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { forkJoin, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { isArrayNullOrEmpty, isElementNull } from '../../../../utils';
import { EVehiclesSortType, EVehicleStatusType, ILabel } from '../../models';
import { delayedRetry } from '../../operators';
import { BalloonService, VehicleSortService } from '../../services';
import {
  EAssetsActions,
  EBalloonInfoActions,
  ECustomerMetadataActions,
  ELayoutActions,
  EMapOptionsActions,
  EMapPlotsActions,
  EVehicleActions,
  GetAssetPlotsSuccess,
  GetGarminStopsPlots,
  GetLabels,
  GetLabelsSuccess,
  GetVehiclePlots,
  GetVehiclePlotsSuccess,
  GetVehiclesSortedSuccess,
  StartGarminStopsPolling,
  StartVehiclesPolling,
  StopVehiclesPolling,
  UpdateVehicleAsDeselectedSuccess,
  UpdateVehicleAsSelectedSuccess,
  UpdateVehiclesSortType,
  UpdateVehiclesSuccess,
  UpdateVehicleStatusFilterType
} from '../actions';
import {
  getAssetPlotLabels,
  getIsGarminStopsDisplayEnabled,
  getIsShowLabelsEnabled,
  getMapHierarchyInfo,
  getMapVehicleMarkers,
  getPlotsInBounds,
  getStatusPanelPlots,
  getVehiclesIdsSorted,
  getVehiclesSortType
} from '../selectors';
import { IAppState } from '../state';

const GET_VEHICLE_INTERVAL = 90000;
const GET_GARMIN_STOPS_INTERVAL = 60000;

@Injectable()
export class VehicleEffects {
  public getVehiclePlotsByPolling$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<StartVehiclesPolling>(EVehicleActions.StartVehiclesPolling),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(() =>
        this._store.pipe(
          select(getMapHierarchyInfo),
          filter(mapHierarchyInfo => mapHierarchyInfo.mapHierarchy.length > 0),
          take(1)
        )
      ),
      switchMap(() =>
        timer(0, GET_VEHICLE_INTERVAL).pipe(
          takeUntil(this._actions$.pipe(ofType<StopVehiclesPolling>(EVehicleActions.StopVehiclesPolling)))
        )
      ),
      map(() => new GetVehiclePlots({ isPolling: true }))
    )
  );

  public getGarminStopsByPolling$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<StartGarminStopsPolling>(EMapPlotsActions.StartGarminStopsPolling),
      filter(() => isPlatformBrowser(this._platformId)),
      switchMap(() =>
        timer(0, GET_GARMIN_STOPS_INTERVAL).pipe(
          takeUntil(
            this._store.pipe(
              select(getIsGarminStopsDisplayEnabled),
              filter(isGarminStopsDisplayEnabled => !isGarminStopsDisplayEnabled)
            )
          )
        )
      ),
      map(() => new GetGarminStopsPlots())
    )
  );

  public getMapVehiclesLabels$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetLabels>(EBalloonInfoActions.GetLabels),
      withLatestFrom(
        this._store.pipe(select(getPlotsInBounds)),
        this._store.pipe(select(getAssetPlotLabels)),
        this._store.pipe(select(getIsShowLabelsEnabled))
      ),
      filter(([, , , isLabelEnabled]) => isLabelEnabled),
      switchMap(([, plotsInBounds, assetPlotLabels]) => {
        const labels: Observable<ILabel[]> = !isArrayNullOrEmpty(plotsInBounds) ? this._balloonService.getLabels(plotsInBounds) : of([]);
        const assetPlotLabelsObsrvable = of(assetPlotLabels);

        const labelObservable: Observable<ILabel[]> = labels.pipe(
          delayedRetry(),
          catchError(() => of([]))
        );

        return forkJoin([assetPlotLabelsObsrvable, labelObservable]);
      }),
      map(([assetPlotLabelsObsrvable, labelObservable]) => [...assetPlotLabelsObsrvable, ...labelObservable]),
      filter(response => !isArrayNullOrEmpty(response)),
      map((labels: ILabel[]) => new GetLabelsSuccess(labels))
    )
  );

  public updateVehicleStatusFilterType$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehicleStatusFilterType>(EMapOptionsActions.UpdateVehicleStatusFilterType),
      withLatestFrom(this._store.pipe(select(getMapVehicleMarkers)), this._store.pipe(select(getIsShowLabelsEnabled))),
      filter(([, , isLabelEnabled]) => isLabelEnabled),
      switchMap(([action, allPlots]) => {
        const labels = this._balloonService.getLabels(
          allPlots.filter(plot => action.payload === EVehicleStatusType.All || plot.status === action.payload)
        );
        return labels.pipe(
          delayedRetry(),
          catchError(() => of(null))
        );
      }),
      filter(response => !isElementNull(response)),
      map((labels: ILabel[]) => new GetLabelsSuccess(labels))
    )
  );

  public updateVehiclesSelectedSuccess$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehicleAsSelectedSuccess | UpdateVehicleAsDeselectedSuccess>(
        ECustomerMetadataActions.UpdateVehicleAsSelectedSuccess,
        ELayoutActions.UpdateVehicleAsDeselectedSuccess
      ),
      switchMap(() => [new GetVehiclePlots(), new GetGarminStopsPlots()])
    )
  );

  public sortStatusPanelItems$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateVehiclesSortType | GetVehiclePlotsSuccess | GetAssetPlotsSuccess | UpdateVehiclesSuccess>(
        EVehicleActions.UpdateVehiclesSortType,
        EMapPlotsActions.GetVehiclePlotsSuccess,
        EAssetsActions.GetAssetPlotsSuccess,
        EVehicleActions.UpdateVehiclesSuccess
      ),
      withLatestFrom(
        this._store.pipe(select(getStatusPanelPlots)),
        this._store.pipe(select(getVehiclesSortType)),
        this._store.pipe(select(getVehiclesIdsSorted))
      ),
      filter(
        ([action, statusPanelItems, sortType, vehiclesIdsSorted]) =>
          // hasn't been sorted before or vehicles added / removed
          statusPanelItems.length !== vehiclesIdsSorted.length ||
          // always update sorting when sorting type changes
          action.type === EVehicleActions.UpdateVehiclesSortType ||
          // if it is a vehicle / assets update, only update if the sorting is in:
          sortType === EVehiclesSortType.LastUpdate ||
          sortType === EVehiclesSortType.Status
      ),
      map(([, statusPanelItems, type]) => this._sortService.sortItems(type, statusPanelItems)),
      filter(itemsIds => !isElementNull(itemsIds)),
      map((itemsIds: number[]) => new GetVehiclesSortedSuccess(itemsIds))
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<IAppState>,
    private readonly _sortService: VehicleSortService,
    private readonly _balloonService: BalloonService,
    @Inject(PLATFORM_ID) private readonly _platformId: object
  ) {}
}
